import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { zoullooColors } from '../../theme';
import {
  useProjectDocuments,
  useUploadProjectDocument,
  useDeleteProjectDocument
} from '../../hooks/useProjectDocuments';
import { useAuthProvider } from '../../auth/hooks/auth-provider';
import { UserType } from '../../models/UserType';
import { UserRole } from '../../models/UserRole';
import { convertFileToBase64 } from '../../utils/file';
import DocumentTable, { DocumentItem } from '../common/DocumentTable';
import { getAssociatedUsersForProject } from '../../api/projects-api';
import { ProjectUser } from '../../models/ProjectUser';

interface ProjectDocumentsProps {
  projectId: number;
  projectStatus: string;
  createdById?: number;
  autoOpenAdd?: boolean;
}

const ProjectDocuments: React.FC<ProjectDocumentsProps> = ({
  projectId,
  createdById,
  autoOpenAdd = false
}) => {
  const { t } = useTranslation();
  const { user } = useAuthProvider();

  const [isAddingDocument, setIsAddingDocument] = useState(false);
  const [addingDocument, setAddingDocument] = useState<DocumentItem | null>(null);
  const [associatedUsers, setAssociatedUsers] = useState<ProjectUser[]>([]);
  const [loadingAssociatedUsers, setLoadingAssociatedUsers] = useState(true);
  const hasAutoOpened = React.useRef(false);

  // Hooks
  const { data: documents = [], isLoading } = useProjectDocuments(projectId);
  const uploadMutation = useUploadProjectDocument();
  const deleteMutation = useDeleteProjectDocument();

  // Load associated users when component mounts
  useEffect(() => {
    const loadAssociatedUsers = async () => {
      try {
        setLoadingAssociatedUsers(true);
        const users = await getAssociatedUsersForProject(projectId);
        setAssociatedUsers(users);
      } catch (error) {
        console.error('Error loading associated users:', error);
      } finally {
        setLoadingAssociatedUsers(false);
      }
    };

    loadAssociatedUsers();
  }, [projectId]);

  // Auto-open add document form if requested
  useEffect(() => {
    if (autoOpenAdd && !hasAutoOpened.current && !loadingAssociatedUsers) {
      hasAutoOpened.current = true;
      setIsAddingDocument(true);
      setAddingDocument({
        name: '',
        urlPath: '',
        content: '',
        type: null
      });
    }
  }, [autoOpenAdd, loadingAssociatedUsers]);

  // Check permissions
  const isInternalUser = user.type === UserType.Internal ||
    user.roles?.includes(UserRole.CSM) ||
    user.roles?.includes(UserRole.Admin);

  // Check if user is associated with the project (creator or contact)
  const isUserAssociatedWithProject = user.id === createdById ||
    associatedUsers.some(u => u.id === user.id);

  // For freelancers, show the button and let backend verify they're associated
  const isFreelancer = user.type === UserType.Freelancer;

  // Can manage documents if: admin/CSM, associated with project, or freelancer (backend will verify)
  const canManageDocuments = isInternalUser || isUserAssociatedWithProject || isFreelancer;

  // Convert ProjectDocument to DocumentItem format for the table
  const documentItems: DocumentItem[] = documents.map(doc => ({
    id: doc.id,
    name: doc.name,
    urlPath: doc.urlPath,
    content: doc.content,
    type: doc.type ? { id: 0, label: doc.type.label } : null,
    projectId: doc.projectId,
    uploadedByUserId: doc.uploadedByUserId,
    uploadedBy: doc.uploadedByUserName,
    uploadedAt: doc.uploadedAt
  }));

  // All useCallback hooks must be defined before any conditional returns
  const handleAddNewDocument = useCallback(() => {
    setIsAddingDocument(true);
    setAddingDocument({
      name: '',
      urlPath: '',
      content: '',
      type: null
    });
  }, []);

  const handleFileUpload = useCallback(async (file: File) => {
    try {
      if (!file) return;

      // Validate file size (10MB max)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error(t('project.fileSizeError'));
      }

      const base64Content = await convertFileToBase64(file);
      setAddingDocument(prev => ({
        ...prev!,
        name: file.name,
        urlPath: URL.createObjectURL(file),
        content: base64Content
      }));
    } catch (error) {
      console.error('Error converting file to base64:', error);
    }
  }, [t]);

  const handleConfirmAddDocument = useCallback(() => {
    if (addingDocument?.name && addingDocument?.content) {
      uploadMutation.mutate({
        projectId,
        document: {
          name: addingDocument.name,
          content: addingDocument.content,
          type: addingDocument.type?.label
        }
      });
      
      setAddingDocument(null);
      setIsAddingDocument(false);
    }
  }, [addingDocument, projectId, uploadMutation]);

  const handleCancelAddDocument = useCallback(() => {
    setAddingDocument(null);
    setIsAddingDocument(false);
  }, []);

  const handleRemoveDocument = useCallback((index: number) => {
    const document = documentItems[index];
    if (document.id) {
      deleteMutation.mutate({
        projectId,
        documentId: document.id
      });
    }
  }, [documentItems, projectId, deleteMutation]);

  // URL generator for project documents
  const getProjectDocumentUrl = useCallback((document: DocumentItem): string => {
    if (document.content) {
      return document.urlPath;
    }

    if (document.urlPath.startsWith('http')) {
      return document.urlPath;
    }

    const baseUrl = process.env.REACT_APP_ZOULLOO_FILE_STORAGE_BASE_URL;
    const containerName = process.env.REACT_APP_PROJECT_DOCUMENTS_CONTAINER_NAME || 'project-documents';

    return `${baseUrl}/${containerName}/${document.urlPath}`;
  }, []);

  // Show loading state
  if (isLoading || loadingAssociatedUsers) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" color={zoullooColors.secondary.dark}>
          {t('project.documents.title')}
        </Typography>

        {/* Show Add button only if not in auto-open mode and user has permissions */}
        {!autoOpenAdd && canManageDocuments && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddNewDocument}
            disabled={isAddingDocument}
            sx={{
              borderColor: zoullooColors.secondary.main,
              color: zoullooColors.secondary.main,
              backgroundColor: 'white',
              '&:hover': {
                borderColor: zoullooColors.secondary.main,
                backgroundColor: zoullooColors.secondary.alpha,
              },
            }}
          >
            {t('project.documents.addDocument')}
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      {!canManageDocuments && documents.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('project.documents.noDocuments')}
        </Alert>
      )}

      {(documents.length > 0 || isAddingDocument) && (
        <DocumentTable
          documents={documentItems}
          isVisible={true}
          isAddingDocument={isAddingDocument}
          addingDocument={addingDocument}
          onAddNewDocument={handleAddNewDocument}
          onFileUpload={handleFileUpload}
          onConfirmAddDocument={handleConfirmAddDocument}
          onCancelAddDocument={handleCancelAddDocument}
          onRemoveDocument={handleRemoveDocument}
          getDocumentUrl={getProjectDocumentUrl}
          fullWidth={true}
          fileAccept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
          showFileUpload={canManageDocuments}
          currentUserId={user.id}
        />
      )}

      {documents.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            {t('project.documents.documentsCount', { count: documents.length })}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ProjectDocuments;